import { useMemo, useState, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import Table from "../../../../Components/Table/Table";
import Card from "./Card";
import { useToast } from "../../../../Components/Common/Toast/ToastContext";
import TableSkeleton from "Components/Common/Skeleton/TableSkeleton";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import EmptyDrawHistoryIcon from "Assets/Img/EmptyDrawHistoryIcon";
import Button from "Components/Common/Button/Button";
import CustomModal from "Components/Common/CustomModal/CustomModal";
import TextArea from "Components/Common/TextArea/TextArea";
import { cancelDraw } from "../../../../services/drawService";
import { drawStatus, CHAR_LIMIT } from "../../../../utils/Common/Constant";

interface AmountObj {
  amount?: number;
  currency?: { symbol?: string };
}

export interface Draw {
  id: string;
  drawNumber?: number;
  status?: string;
  toDate?: string;
  requestedAmount?: AmountObj;
  retainageAmount?: AmountObj;
  retainageThreshold?: string;
  approvedAmount?: AmountObj;
  ownerSignatureUrl?: string | null;
  stored?: AmountObj;
}

interface DrawRow {
  id: string;
  rawStatus: string | undefined;
  draw: number | string;
  period: string;
  amount: string;
  retainage: string;
  netPaid: string;
  ownerSig: string | null | undefined;
  status: string;
  statusVariant: string;
}

interface DrawHistoryTableProps {
  projectId: string;
  draws?: Draw[];
  loading?: boolean;
  onSubmit: () => void;
  projectStatus: string;
  onCancelled?: () => void;
}

const STATUS_VARIANT: Record<string, string> = {
  owner_signature_pending: "pending",
  inspection_pending: "pending",
  approved: "active",
  cancelled: "danger",
  submitted: "pending",
  draft: "pending",
};

const TERMINAL_STATUSES = [
  drawStatus.APPROVED,
  drawStatus.DENIED,
  drawStatus.CANCELLED,
];

const formatDate = (val?: string): string => {
  if (!val) return "-";
  const d = new Date(val);
  return isNaN(d.getTime())
    ? val
    : d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

const formatAmount = (amountObj?: AmountObj): string => {
  if (!amountObj) return "-";
  const symbol = amountObj.currency?.symbol ?? "$";
  return `${symbol}${Number(amountObj.amount ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
};

export const formatStatus = (status?: string): string =>
  status?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) ?? "-";

export default function DrawHistoryTable({
  projectId,
  draws = [],
  loading = false,
  projectStatus,
  onSubmit,
  onCancelled,
}: DrawHistoryTableProps) {
  const navigate = useNavigate();
  const showToast = useToast();
  const [cancelTarget, setCancelTarget] = useState<DrawRow | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const rows = useMemo<DrawRow[]>(
    () =>
      draws.map((d) => ({
        id: d.id,
        rawStatus: d.status,
        draw: d.drawNumber ?? "-",
        period: formatDate(d.toDate),
        amount: formatAmount(d.requestedAmount),
        retainage:
          d.retainageAmount != null ? formatAmount(d.retainageAmount) : "-",
        netPaid:
          d.approvedAmount != null ? formatAmount(d.approvedAmount) : "-",
        ownerSig: d.ownerSignatureUrl,
        status: formatStatus(d.status),
        statusVariant: STATUS_VARIANT[d.status ?? ""] ?? "",
      })),
    [draws],
  );

  const retainagePct = draws.find(
    (d) => d.retainageThreshold,
  )?.retainageThreshold;
  const retainageHeader = retainagePct
    ? `Retainage (${parseFloat(retainagePct)}%)`
    : "Retainage";

  const handleRowClick = (row: DrawRow) => {
    if (row.status === "Draft") {
      navigate(`/draw-request/${projectId}/${row.id}`);
    }
  };

  const handleSubmitClick = () => {
    if (projectStatus === "completed") {
      showToast(
        "This project has been completed. No further draw requests can be submitted.",
      );
      return;
    }
    onSubmit();
  };

  const handleCancelClick = (event: MouseEvent, row: DrawRow) => {
    event.stopPropagation();
    setCancelTarget(row);
    setCancelReason("");
  };

  const handleCancelClose = () => {
    if (cancelling) return;
    setCancelTarget(null);
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    if (!cancelReason.trim()) {
      showToast("Please provide a reason for cancelling this draw.");
      return;
    }
    setCancelling(true);
    try {
      const res: any = await cancelDraw(projectId, cancelTarget.id, {
        reason: cancelReason.trim(),
      });
      if (res?.error) {
        showToast(res.error.message || "Failed to cancel draw.");
        return;
      }
      showToast("Draw cancelled successfully.");
      setCancelTarget(null);
      onCancelled?.();
    } catch {
      showToast("Failed to cancel draw.");
    } finally {
      setCancelling(false);
    }
  };

  const columns = [
    {
      header: "Draw #",
      render: (row: DrawRow) => (
        <div className={`drawNumber ${row.statusVariant}`}>DRAW {row.draw}</div>
      ),
    },
    {
      header: "Period To",
      className: "right",
      render: (row: DrawRow) => row.period,
    },
    {
      header: "Amount Billed",
      className: "right",
      render: (row: DrawRow) => row.amount,
    },
    {
      header: retainageHeader,
      className: "right",
      render: (row: DrawRow) => row.retainage,
    },
    {
      header: "Net Paid",
      className: "right",
      render: (row: DrawRow) => row.netPaid,
    },
    {
      header: "Owner Sig.",
      className: "right",
      render: (row: DrawRow) => (
        <span className={`badge ${row.ownerSig ? "active" : "defaultSign"} sm`}>
          {row.ownerSig ? "Signed" : "-"}
        </span>
      ),
    },
    {
      header: "Status",
      className: "right",
      render: (row: DrawRow) => (
        <span className={`badge default ${row.statusVariant} sm`}>
          {row.status === "Draft" ? "Continue" : row.status}
        </span>
      ),
    },
    {
      header: "Actions",
      className: "right",
      render: (row: DrawRow) =>
        !TERMINAL_STATUSES.includes(row.rawStatus ?? "") ? (
          <Button
            className="cancel-draw-btn"
            onClick={(e) => handleCancelClick(e, row)}
          >
            Cancel Draw
          </Button>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <>
      <Card
        title="Draw History"
        action={
          <Button className="btn teal sm mb-3" onClick={handleSubmitClick}>
            + Submit New Draw
          </Button>
        }
      >
        {projectStatus === "completed" && (
          <div className="completedBanner">
            ⚠️
            <span className="completedBannerText">
              This project has been completed. No further draw requests can be
              submitted.
            </span>
          </div>
        )}
        {loading ? (
          <TableSkeleton cols={8} rows={4} />
        ) : (
          <div className="">
            <Table
              data={rows}
              columns={columns}
              rowClassName={(row) =>
                row.statusVariant === "pending" ? "pending" : ""
              }
              onRowClick={handleRowClick}
              fallbackUI={
                <EmptyState
                  noBorder={true}
                  height={"100px"}
                  icon={<EmptyDrawHistoryIcon />}
                  title="No Draws on Record"
                  subtitle="Draw requests submitted for this project will appear here once the first disbursement is initiated."
                />
              }
            />
          </div>
        )}
      </Card>

      <CustomModal
        show={!!cancelTarget}
        handleClose={handleCancelClose}
        isHeaderVisible
        isCrossBtn={true}
        isFooterVisible
        title={`Cancel Draw #${String(cancelTarget?.draw ?? "").padStart(2, "0")}?`}
        footerContent={
          <>
            <Button
              className="cancel-draw-btn keep-draw-btn"
              onClick={handleCancelClose}
              disabled={cancelling}
            >
              Keep Draw
            </Button>
            <Button
              className="cancel-draw-btn cancel-draw-model-btn"
              onClick={handleCancelConfirm}
              disabled={cancelling}
            >
              {cancelling ? (
                <>
                  {" "}
                  <span className="btn-spinner" />
                  Cancelling…
                </>
              ) : (
                "Confirm Cancellation"
              )}
            </Button>
          </>
        }
      >
        <p>
          Provide a reason for cancelling Draw #
          {String(cancelTarget?.draw ?? "").padStart(2, "0")}. Your note will be
          shared with CSC New England and the Owner.
        </p>
        <TextArea
          className="form-control mt-3"
          placeholder="e.g. Duplicate submission, resubmitting with corrected line items…"
          rows={4}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          disabled={cancelling}
          maxChars={CHAR_LIMIT.DESCRIPTION}
        />
      </CustomModal>
    </>
  );
}
